import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  getAllThirdPartyAccess,
  createThirdPartyAccess as createService,
  updateThirdPartyAccess as updateService,
  deleteThirdPartyAccess as deleteService,
  syncThirdPartyAccess as syncService,
  getThirdPartyAccessSyncHistory as historyService
} from '../../services/tenant/third-party-access.service'
import { validate } from '../../utils'
import { handlerErrorV2 } from '../../utils/errors'
import {
  updateThirdPartyAccessList,
  updateThirdPartyAccessListMethod
} from '../../store/actions/tenant.actions'
import isEmpty from 'lodash/isEmpty'

/**
 * Hook de credenciales de acceso a terceros (MF-507). CRUD + disparo de
 * sincronización + historial. La contraseña es write-only: solo se envía, nunca
 * se recibe (el backend devuelve `hasPassword`).
 */
const useThirdPartyAccess = () => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [errors, setErrors] = useState({})

  const fetchList = useCallback(async () => {
    setIsLoadingList(true)
    try {
      const response = await getAllThirdPartyAccess()
      dispatch(updateThirdPartyAccessList(response?.data || []))
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred' })
    } finally {
      setIsLoadingList(false)
    }
  }, [dispatch])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  // En create la contraseña es obligatoria; en edit es opcional (se conserva la existente).
  const validateForm = (form, { requirePassword = true } = {}) => {
    const fieldRules = {
      baseUrl: 'required',
      schoolSlug: 'required',
      username: 'required'
    }
    if (requirePassword) fieldRules.password = 'required'

    const validationErrors = validate(fieldRules, form)
    if (!isEmpty(validationErrors)) {
      return { error: true, ...validationErrors }
    }
    return { error: false }
  }

  const create = async (form = {}) => {
    const result = validateForm(form, { requirePassword: true })
    if (result.error) return result

    try {
      const response = await createService(form)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      }
      dispatch(updateThirdPartyAccessListMethod('POST', response.data))
      enqueueSnackbar('Credential created successfully', { variant: 'success' })
      return { error: false }
    } catch (error) {
      return { error: true, message: handlerErrorV2(error) }
    }
  }

  const update = async (id, form = {}) => {
    // Si no mandan password nueva, no se valida ni se reenvía (write-only).
    const requirePassword = Boolean(form.password)
    const result = validateForm(form, { requirePassword })
    if (result.error) return result

    const payload = { ...form }
    if (!payload.password) delete payload.password

    try {
      const response = await updateService(id, payload)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      }
      dispatch(updateThirdPartyAccessListMethod('PUT', response.data))
      enqueueSnackbar('Credential updated successfully', { variant: 'success' })
      return { error: false }
    } catch (error) {
      return { error: true, message: handlerErrorV2(error) }
    }
  }

  const remove = async (id) => {
    try {
      const response = await deleteService(id)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      }
      dispatch(updateThirdPartyAccessListMethod('DELETE', id))
      enqueueSnackbar('Credential deleted successfully', { variant: 'success' })
      return { error: false }
    } catch (error) {
      return { error: true, message: handlerErrorV2(error) }
    }
  }

  const sync = async (id) => {
    try {
      const response = await syncService(id)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      }
      enqueueSnackbar('Sync started — check the history in a few minutes', {
        variant: 'info'
      })
      return { error: false, data: response.data }
    } catch (error) {
      return { error: true, message: handlerErrorV2(error) }
    }
  }

  const getHistory = async (id, limit = 20) => {
    try {
      const response = await historyService(id, limit)
      return { error: false, data: response?.data || [] }
    } catch (error) {
      return { error: true, message: handlerErrorV2(error), data: [] }
    }
  }

  return {
    errors,
    isLoadingList,
    create,
    update,
    remove,
    sync,
    getHistory
  }
}

export default useThirdPartyAccess
